import { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import cloneDeep from 'lodash/cloneDeep';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { MenuItemConfig } from 'common/types';
import { AccessLevel, SavedSearch, SavedSearchType } from 'common/graphqlTypes';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import Dropdown from 'components/common/Dropdown/Dropdown';
import ElementWithTooltip from 'components/common/ElementWithTooltip/ElementWithTooltip';
import Link from 'components/common/Link/Link';
import TruncateWithTooltip from 'components/common/TruncateWithTooltip/TruncateWithTooltip';
import ConfirmDialog from 'components/common/Dialog/ConfirmDialog';
import SaveSearchDialog from 'components/search/SaveSearchDialog';

import { powerQueriesPageUrlState, searchLogsPageUrlState } from 'components/common/Managers/RouteStateManager.state';
import { savedSearchesState } from 'components/account/account.state';
import { DELETE_SEARCH } from 'components/other/MainNavBar/queries';

type NavBarSearchDropdownProps = {
  isActive: boolean;
  testId?: ELEMENT;
  sharedSearchPermissions?: AccessLevel;
};

/**
 * Search dropdown for the navbar
 *
 * @prop isActive          Adds the 'active' class if true
 * @prop privateSearches   List of private searches
 * @prop sharedSearches    List of shared searches
 * @prop labs              List of lab config objects
 * @prop deleteHandler     Function to call when user deletes a search
 * @prop editHandler       Function to call when user edits a search
 * @prop testId            Test id for testing
 *
 * @returns {Fragment}                The Search dropdown component
 */
const NavBarSearchDropdown = ({
  isActive,
  testId,
  sharedSearchPermissions,
}: NavBarSearchDropdownProps): JSX.Element => {
  const [savedSearches, setSavedSearches] = useRecoilState(savedSearchesState);
  const powerQueriesPageUrl = useRecoilValue(powerQueriesPageUrlState);
  const searchLogsPageUrl = useRecoilValue(searchLogsPageUrlState);
  const isSearchTracesEnabled = useIsLabEnabled(K.LAB.SEARCH_TRACES);
  const isPowerQueriesEnabled = useIsLabEnabled(K.LAB.POWER_QUERIES);

  const [showSavedSearchDeleteDialog, setShowSavedSearchDeleteDialog] = useState(false);
  const [showSaveSearchDialog, setShowSaveSearchDialog] = useState(false);
  const [currentSavedSearch, setCurrentSavedSearch] = useState<SavedSearch | null>(null);
  const [currentSavedSearchType, setCurrentSavedSearchType] = useState(SavedSearchType.PRIVATE);
  const savedSearch = useRef({ type: SavedSearchType.PRIVATE });

  // user has clicked trash icon; show confirm dialog
  const deleteHandler = (menuItem: MenuItemConfig) => {
    const savedSearch = menuItem?.item?.search;
    if (savedSearch) {
      setCurrentSavedSearch(savedSearch);
      setCurrentSavedSearchType(menuItem?.item?.type);
      setShowSavedSearchDeleteDialog(true);
    }
  };

  // user has clicked edit icon; populate SaveSearchDialog
  const editHandler = (menuItem: MenuItemConfig) => {
    const savedSearch = menuItem?.item?.search;
    if (savedSearch) {
      // clone search so we can add type; we need to know original type for the delete call in our two-part edit action
      const search = cloneDeep(savedSearch);
      search.type = menuItem?.item?.type;
      setCurrentSavedSearch(search);
      setCurrentSavedSearchType(menuItem?.item?.type);
      setShowSaveSearchDialog(true);
    }
  };

  // delete a saved search
  // refetching costs a network request but is much easier than updating Apollo cache and guaranteed to be accurate
  const [deleteSavedSearch] = useMutation(DELETE_SEARCH, {
    onCompleted: (data) => {
      if (savedSearch?.current && savedSearch.current.type) {
        // @ts-expect-error
        setSavedSearches({ [savedSearch.current.type]: data?.deleteSavedSearchV2 });
      }
    },
  });

  const doDeleteSavedSearch = () => {
    if (currentSavedSearch) {
      savedSearch.current.type = currentSavedSearchType;
      deleteSavedSearch({
        variables: { name: currentSavedSearch.name, type: currentSavedSearchType, index: currentSavedSearch.index },
      });
      setCurrentSavedSearch(null);
      setCurrentSavedSearchType(SavedSearchType.PRIVATE);
    }
  };

  const currentSavedSearchName = currentSavedSearch ? currentSavedSearch.name : '';

  // dialog for editing a saved search
  const saveSearchDialog = (
    <SaveSearchDialog
      action={K.ACTION.EDIT}
      searchType={currentSavedSearchType}
      savedSearchToEdit={currentSavedSearch || undefined}
      show={showSaveSearchDialog}
      close={() => setShowSaveSearchDialog(false)}
    />
  );

  const deleteSearchDialog = (
    <ConfirmDialog
      title="Delete Saved Search"
      text={`Delete saved search "${currentSavedSearchName}" ?`}
      show={showSavedSearchDeleteDialog}
      close={() => setShowSavedSearchDeleteDialog(false)}
      onSubmit={doDeleteSavedSearch}
    />
  );

  const sections: MenuItemConfig[][] = [
    [
      {
        text: 'New Search',
        href: searchLogsPageUrl,
        testId: ELEMENT.navSearchNewSearch,
      },
    ],
  ];

  let section: MenuItemConfig[] = [];
  if (isSearchTracesEnabled) {
    section.push({
      text: 'Search Traces',
      href: K.ROUTE.TRACE,
      testId: ELEMENT.navSearchTraces,
    });
  }
  if (isPowerQueriesEnabled) {
    section.push({
      text: 'PowerQueries',
      href: powerQueriesPageUrl,
      testId: ELEMENT.navSearchPowerQueries,
    });
  }
  sections.push(section);

  const privateSearches = savedSearches[SavedSearchType.PRIVATE];
  const sharedSearches = savedSearches[SavedSearchType.SHARED];

  // construct Save Searches list
  section = [
    {
      text: 'SAVED SEARCHES',
      testId: ELEMENT.navSearchSavedSearches,
    },
  ];
  section = section.concat(
    privateSearches.map((search, index) => ({
      children: <TruncateWithTooltip text={search.name} id={search.name} maxLength={30} />,
      text: search.name,
      href: search.url,
      item: { search, type: SavedSearchType.PRIVATE, testId: createTestId(ELEMENT.navSearchSavedSearch, index) },
      hoverActions: [
        {
          icon: (
            <ElementWithTooltip tooltip="Edit">
              <EditIcon />
            </ElementWithTooltip>
          ),
          action: editHandler,
          testId: ELEMENT.navSearchSavedSearchEdit,
        },
        {
          icon: (
            <ElementWithTooltip tooltip="Delete">
              <DeleteIcon />
            </ElementWithTooltip>
          ),
          action: deleteHandler,
          testId: ELEMENT.navSearchSavedSearchDelete,
        },
      ],
    })),
  );

  sections.push(section);

  // construct Shared Searches list
  section = [
    {
      text: 'SHARED SEARCHES',
      testId: ELEMENT.navSearchSharedSearches,
    },
  ];
  section = section.concat(
    sharedSearches.map((search, index) => ({
      children: <TruncateWithTooltip text={search.name} id={search.name} maxLength={30} />,
      text: search.name,
      href: search.url,
      item: { search, type: SavedSearchType.SHARED, testId: createTestId(ELEMENT.navSearchSharedSearch, index) },
      ...(sharedSearchPermissions === AccessLevel.WRITE && {
        hoverActions: [
          {
            icon: <EditIcon />,
            action: editHandler,
            testId: ELEMENT.navSearchSharedSearchEdit,
          },
          {
            icon: <DeleteIcon />,
            action: deleteHandler,
            testId: ELEMENT.navSearchSharedSearchDelete,
          },
        ],
      }),
    })),
  );
  sections.push(section);

  return (
    <>
      <Dropdown isNav className={isActive ? 'active' : ''} sections={sections} itemAs={Link} testId={testId}>
        {K.ROUTE_NAME.SEARCH}
      </Dropdown>
      {saveSearchDialog}
      {deleteSearchDialog}
    </>
  );
};

export default NavBarSearchDropdown;
